<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>settings</md-icon>
          </div>
          <h4 class="title">Global Settings</h4>
        </md-card-header>

        <md-card-content v-if="loading">
          <p>Loading settings...</p>
        </md-card-content>

        <md-card-content v-else>

          <!-- Tab buttons -->
          <div class="settings-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              :class="['tab-btn', { active: activeTab === tab.key }]"
              @click="activeTab = tab.key"
            >
              <md-icon style="font-size:16px; margin-right:6px;">{{ tab.icon }}</md-icon>
              {{ tab.label }}
            </button>
          </div>

          <!-- Contact Settings -->
          <div v-if="activeTab === 'contact'" class="settings-group">
            <h5 class="group-title">Contact Information</h5>
            <p class="group-desc">Displayed on the Contact Us page of the website.</p>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Contact Email</label>
                  <md-input v-model="form.contact_email" type="email" placeholder="hello@beesatelier.art" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Contact Phone</label>
                  <md-input v-model="form.contact_phone" type="text" placeholder="+91 98765 43210" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Website URL</label>
                  <md-input v-model="form.contact_website" type="text" placeholder="https://beesatelier.art" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Address</label>
                  <md-textarea v-model="form.contact_address" placeholder="Full address..." />
                </md-field>
              </div>
            </div>
          </div>

          <!-- Social Settings -->
          <div v-if="activeTab === 'social'" class="settings-group">
            <h5 class="group-title">Social Media Links</h5>
            <p class="group-desc">These links appear in the website footer and contact page.</p>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Instagram URL</label>
                  <md-input v-model="form.social_instagram" type="text" placeholder="https://instagram.com/..." />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Facebook URL</label>
                  <md-input v-model="form.social_facebook" type="text" placeholder="https://facebook.com/..." />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Pinterest URL</label>
                  <md-input v-model="form.social_pinterest" type="text" placeholder="https://pinterest.com/..." />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Twitter / X URL</label>
                  <md-input v-model="form.social_twitter" type="text" placeholder="https://twitter.com/..." />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>WhatsApp Number (with country code)</label>
                  <md-input v-model="form.social_whatsapp" type="text" placeholder="919876543210" />
                </md-field>
              </div>
            </div>
          </div>

          <!-- Email Settings -->
          <div v-if="activeTab === 'email'" class="settings-group">
            <h5 class="group-title">Email Settings</h5>
            <p class="group-desc">Used in transactional emails sent to customers.</p>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>From Name</label>
                  <md-input v-model="form.email_from_name" type="text" placeholder="Bee's Atelier" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Support Email</label>
                  <md-input v-model="form.email_support" type="email" placeholder="support@beesatelier.art" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Email Footer Text</label>
                  <md-input v-model="form.email_footer_text" type="text" placeholder="Handcrafted with love." />
                </md-field>
              </div>
            </div>
          </div>

          <!-- General Settings -->
          <div v-if="activeTab === 'general'" class="settings-group">
            <h5 class="group-title">General Settings</h5>
            <p class="group-desc">Basic site-wide configuration.</p>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Site Name</label>
                  <md-input v-model="form.site_name" type="text" placeholder="Bee's Atelier" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Site Tagline</label>
                  <md-input v-model="form.site_tagline" type="text" placeholder="Handcrafted with love" />
                </md-field>
              </div>
              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Logo URL</label>
                  <md-input v-model="form.site_logo_url" type="text" placeholder="/images/logo.png" />
                </md-field>
              </div>
            </div>
          </div>

        </md-card-content>

        <md-card-actions md-alignment="left">
          <md-button
            class="md-success"
            :disabled="isSaving"
            @click="saveSettings"
          >
            {{ isSaving ? 'Saving...' : 'Save Settings' }}
            <span v-if="isSaving"><i class="fa fa-spinner fa-spin" /></span>
          </md-button>
        </md-card-actions>
      </md-card>
    </div>
  </div>
</template>

<script>
import SettingsService from "../../services/SettingsService";

export default {
  name: "GlobalSettings",
  data() {
    return {
      loading: true,
      isSaving: false,
      activeTab: "contact",
      tabs: [
        { key: "contact", label: "Contact",  icon: "contact_mail" },
        { key: "social",  label: "Social",   icon: "share" },
        { key: "email",   label: "Email",    icon: "email" },
        { key: "general", label: "General",  icon: "tune" },
      ],
      form: {
        // contact
        contact_email:   "",
        contact_phone:   "",
        contact_address: "",
        contact_website: "",
        // social
        social_instagram: "",
        social_facebook:  "",
        social_pinterest: "",
        social_twitter:   "",
        social_whatsapp:  "",
        // email
        email_from_name:   "",
        email_support:     "",
        email_footer_text: "",
        // general
        site_name:      "",
        site_tagline:   "",
        site_logo_url:  "",
      },
    };
  },
  methods: {
    loadSettings() {
      this.loading = true;
      SettingsService.getAdminSettings()
        .then((response) => {
          const flat = response.data.data.flat;
          // Map each key into the form
          Object.keys(this.form).forEach((key) => {
            if (flat[key] !== undefined) {
              this.form[key] = flat[key];
            }
          });
          this.loading = false;
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.response_msg || "Failed to load settings");
          this.loading = false;
        });
    },

    saveSettings() {
      this.isSaving = true;
      SettingsService.updateSettings({ settings: this.form })
        .then(() => {
          this.$toast.success("Settings saved successfully.");
          this.isSaving = false;
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.response_msg || "Failed to save settings");
          this.isSaving = false;
        });
    },
  },
  beforeMount() {
    this.loadSettings();
  },
};
</script>

<style scoped>
.settings-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 28px;
  flex-wrap: wrap;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #718096;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  border-radius: 0;
}

.tab-btn:hover  { color: #4a5568; }
.tab-btn.active { color: #4caf50; border-bottom-color: #4caf50; font-weight: 600; }

.settings-group { padding: 8px 0 16px; }

.group-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
}

.group-desc {
  font-size: 13px;
  color: #a0aec0;
  margin-bottom: 24px;
}
</style>